import { RotateCcw, Save } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { QueryState, describeError } from "@/components/query-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Thresholds, ThresholdsState } from "@/generated/api/model";
import {
    useGetOpportunityThresholds,
    useResetOpportunityThresholds,
    useSetOpportunityThresholds,
} from "@/generated/api/opportunities/opportunities";

import { ada, duration, toLovelace, useInvalidateOpportunities } from "./format";

/**
 * The actionability thresholds. Saved in the bot, not the browser: they define the shared
 * statistics, and every recorded window is re-classified against them on the next read.
 */
export function ThresholdsCard() {
    const thresholds = useGetOpportunityThresholds();
    const invalidate = useInvalidateOpportunities();
    const onSettled = { onSuccess: () => invalidate(), onError: (error: unknown) => toast.error(describeError(error)) };
    const save = useSetOpportunityThresholds({ mutation: onSettled });
    const reset = useResetOpportunityThresholds({ mutation: onSettled });

    return (
        <Card role="region" aria-labelledby="actionable-heading">
            <CardHeader>
                <h2 id="actionable-heading" className="font-semibold">
                    Actionable means
                </h2>
                {thresholds.data && <OverrideBadge state={thresholds.data} />}
            </CardHeader>
            <CardContent>
                <QueryState query={thresholds} isEmpty={() => false} emptyMessage="">
                    {(data) => (
                        <ThresholdsForm
                            key={JSON.stringify(data.effective)}
                            idPrefix="actionable"
                            state={data}
                            summary={(t) => (
                                <>
                                    Open on chain for at least{" "}
                                    <span className="text-foreground">{duration(t.min_duration_secs)}</span> and worth
                                    at least <span className="text-foreground">{ada(t.min_profit_lovelace)} ADA</span>{" "}
                                    at its peak.
                                </>
                            )}
                            pending={save.isPending || reset.isPending}
                            onSave={(data) => save.mutate({ data })}
                            onReset={() => reset.mutate()}
                        />
                    )}
                </QueryState>
            </CardContent>
        </Card>
    );
}

export function OverrideBadge({ state }: { state: ThresholdsState }) {
    return (
        <Badge variant={state.overridden ? "warning" : "default"}>{state.overridden ? "custom" : "env default"}</Badge>
    );
}

/** A threshold pair the bot saves, with the env default it falls back to on reset. */
export function ThresholdsForm({
    idPrefix,
    state,
    summary,
    pending,
    onSave,
    onReset,
}: {
    idPrefix: string;
    state: ThresholdsState;
    summary: (thresholds: Thresholds) => ReactNode;
    pending: boolean;
    onSave: (thresholds: Thresholds) => void;
    onReset: () => void;
}) {
    const [minDuration, setMinDuration] = useState(String(state.effective.min_duration_secs));
    const [minProfit, setMinProfit] = useState(ada(state.effective.min_profit_lovelace));

    const parsed = parse(minDuration, minProfit);
    const env = state.env_default;

    return (
        <form
            className="space-y-3"
            onSubmit={(event) => {
                event.preventDefault();
                if (parsed) onSave(parsed);
            }}
        >
            <p className="text-sm text-muted-foreground">{summary(state.effective)}</p>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label htmlFor={`${idPrefix}-min-duration`}>Min open (s)</Label>
                    <Input
                        id={`${idPrefix}-min-duration`}
                        inputMode="numeric"
                        value={minDuration}
                        onChange={(event) => setMinDuration(event.target.value)}
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor={`${idPrefix}-min-profit`}>Min profit (ADA)</Label>
                    <Input
                        id={`${idPrefix}-min-profit`}
                        inputMode="decimal"
                        value={minProfit}
                        onChange={(event) => setMinProfit(event.target.value)}
                    />
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" disabled={!parsed || pending}>
                    <Save />
                    Save
                </Button>
                {state.overridden && (
                    <Button type="button" size="sm" variant="outline" disabled={pending} onClick={onReset}>
                        <RotateCcw />
                        Reset
                    </Button>
                )}
                <span className="text-xs text-muted-foreground">
                    env: {env.min_duration_secs} s · {ada(env.min_profit_lovelace)} ADA
                </span>
            </div>
        </form>
    );
}

function parse(minDuration: string, minProfit: string): Thresholds | null {
    const secs = Number(minDuration.trim());
    const profit = Number(minProfit.trim().replace(",", "."));
    if (minDuration.trim() === "" || minProfit.trim() === "") return null;
    if (!Number.isInteger(secs) || secs < 0 || !Number.isFinite(profit) || profit < 0) return null;
    return { min_duration_secs: secs, min_profit_lovelace: toLovelace(profit) };
}
